import { ApexInfluencer, TiktokUserImage, InstagramUserImage, CampaignType } from '@round/api';
import { Skeleton } from '@round/ui-kit';
import SearchInput from '../../../../ui/SearchInput/SearchInput';
import React, { useMemo } from 'react';
import { OptionsContext } from '../../../../contexts/OptionsContext';
import useNonNullContext from '../../../../hooks/useNonNullContext';
import useOnScroll from '../../../../hooks/useOnScroll';
import { useApexInfluencersData } from './useApexInfluencerListData';
import RosterInfluencerBar from './components/RosterInfluencerBar/RosterInfluencerBar';
import styles from './RosterList.module.css';
import { useInfluencerTags } from '../../../../hooks/useInfluencerTags';
import useUrlState from '../../../../hooks/useUrlState';
import { showNotification } from '../../../../utils/showNotification';
import RosterFilters, { RosterFiltersObject } from './components/RosterFilters/RosterFilters';
import range from 'lodash/range';
import { parseConditionsFromUrlString } from './components/RosterFilters/CategoriesFilter/CategoriesFilter.helpers';
import { useContentTags } from '../../../../contexts/ContentTagsContext';
import useAbortableEffect from '../../../../hooks/useAbortableEffect';

type RosterListRow = {
    influencer: ApexInfluencer;
    influencerImage: TiktokUserImage | InstagramUserImage | undefined;
};

const RosterList = () => {
    const { currencies } = useNonNullContext(OptionsContext);
    const { tags: contentTags, isInitialized: isContentTagsInitialized, init: initContentTags } = useContentTags();

    useAbortableEffect(
        (signal) => {
            if (!isContentTagsInitialized) {
                initContentTags({ signal });
            }
        },
        [isContentTagsInitialized, initContentTags]
    );

    const { influencerTags } = useInfluencerTags();

    const [urlState, setUrlState] = useUrlState<RosterFiltersObject & { search: string }>(
        {
            search: '',
            min_cost: 50,
            campaign_type: 'AUDIO',
        },
        {
            platforms: 'array-string',
            currency: 'number',
            categories: 'array-number',
            locations: 'array-number',
            min_followers: 'number',
            max_followers: 'number',
            min_cost: 'number',
            max_cost: 'number',
        }
    );

    const tagsParam = useMemo(
        () =>
            parseConditionsFromUrlString(urlState.content_tags).reduce((acc, item) => {
                if (item.type === 'all') {
                    return acc.concat(item.value.map((tag) => [tag]));
                }

                return acc.concat([item.value]);
            }, [] as number[][]),
        [urlState.content_tags]
    );

    const {
        influencersData,
        page,
        hasNextInfluencersPage,
        initialized,
        influencersLoading,
        errorLoadingInfluencers,
        tiktokUserImages,
        instagramUserImages,
        loadNextPage,
    } = useApexInfluencersData({
        currency: Number(urlState.currency),
        campaign_type: urlState.campaign_type as CampaignType,
        search: urlState.search,
        platforms: urlState.platforms?.join(','),
        category_tags: urlState.categories?.join(','),
        location_tags: urlState.locations?.join(','),
        min_followers: urlState.min_followers ? `${urlState.min_followers}` : undefined,
        max_followers: urlState.max_followers ? `${urlState.max_followers}` : undefined,
        min_cost: urlState.min_cost ? `${urlState.min_cost}` : undefined,
        max_cost: urlState.max_cost ? `${urlState.max_cost}` : undefined,
        content_tags: tagsParam,
    });

    const nextPageLoading = initialized && influencersLoading;
    const bottomReached = initialized && page > 1 && !hasNextInfluencersPage;

    const loadNextRows = () => {
        if (influencersLoading || errorLoadingInfluencers || !hasNextInfluencersPage || !initialized) {
            return;
        }
        loadNextPage().catch(() => showNotification('Could not load next page', 'error'));
    };

    const handleScroll = useOnScroll(loadNextRows, { threshold: 600 });

    const rosterListRows: RosterListRow[] = useMemo(
        () =>
            influencersData.map((influencer) => ({
                influencer,
                influencerImage:
                    tiktokUserImages.find((img) => img.user_id === influencer.tiktok_user_id) ||
                    instagramUserImages.find((img) => img.user_id === influencer.instagram_user_id),
            })),
        [influencersData, instagramUserImages, tiktokUserImages]
    );

    return (
        <div className={styles.container} onScroll={handleScroll}>
            <div className={styles.headerBar}>
                <SearchInput
                    value={urlState.search}
                    onChange={(value) => {
                        setUrlState({ search: value });
                    }}
                    className={styles.searchBar}
                    placeholder="Search by username"
                />
                <RosterFilters
                    filters={{
                        platforms: urlState.platforms,
                        categories: urlState.categories,
                        locations: urlState.locations,
                        min_followers: urlState.min_followers,
                        max_followers: urlState.max_followers,
                        max_cost: urlState.max_cost,
                        min_cost: urlState.min_cost,
                        campaign_type: urlState.campaign_type as CampaignType,
                        currency: urlState.currency,
                        content_tags: urlState.content_tags,
                    }}
                    onFilterChange={(val) => setUrlState(val)}
                />
            </div>
            <div className={styles.list}>
                {rosterListRows.map((row) => {
                    return (
                        <RosterInfluencerBar
                            key={row.influencer.id}
                            influencer={row.influencer}
                            influencerImage={row.influencerImage}
                            locationTag={influencerTags.find((tag) => row.influencer.location === tag.id)}
                            contentTags={contentTags.filter((tag) => row.influencer.content_tags.includes(tag.id))}
                            campaignType={urlState.campaign_type as CampaignType}
                            currency={currencies.find((curr) => curr.id === Number(urlState.currency))}
                            readOnly={true}
                        />
                    );
                })}
                {(nextPageLoading || !initialized) &&
                    !errorLoadingInfluencers &&
                    range(0, 6).map((i) => (
                        <Skeleton
                            key={i}
                            className={styles.barSkeleton}
                            containerClassName={styles.skeletonContainer}
                        />
                    ))}
                {bottomReached && (
                    <div className={styles.bottomReachedBar}>
                        <h4>Looks like you've reached the bottom</h4>
                    </div>
                )}
            </div>
        </div>
    );
};

export default RosterList;
